import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BER_OPCIOK } from '../ber.opciok';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';

type ZarMezo = 'zarAlk' | 'zarJv' | 'zarKedv' | 'zarSzocho' | 'zarElt';

// BER alkalmazottak: a kiválasztott ügyfél foglalkoztatottjai, jogviszonyaikkal,
// adó-/járulékkedvezményeikkel és letiltásaikkal.
@Component({
  selector: 'app-ber-alkalmazottak',
  templateUrl: './alkalmazottak.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss'],
})
export class AlkalmazottakComponent implements OnInit, OnDestroy {
  @ViewChild('alkModal') alkModal: IonModal;
  @ViewChild('jvModal') jvModal: IonModal;
  @ViewChild('kedvModal') kedvModal: IonModal;
  @ViewChild('szochoModal') szochoModal: IonModal;
  @ViewChild('eltModal') eltModal: IonModal;
  @ViewChild('navModal') navModal: IonModal;
  @ViewChild('sulModal') sulModal: IonModal;

  alkalmazottak: any[] = [];
  navBejelentettek: any[] = [];       // NAV M2M-ből lekért bejelentett foglalkoztatottak (import)
  navUtolsoSzinkron: string | null = null;
  navBetolt = false;
  ugyfel: any = null;   // az aktív ügyfél törzsadatai (másoláshoz)
  kivalasztott: any = null;
  jogviszonyok: any[] = [];
  kedvezmenyek: any[] = [];
  szochoKedvezmenyek: any[] = [];
  /*  SZABADSÁG (ber-szabadsag.js): az aktív év kerete fajtánként, a felhasználás a jelenlétből. A kézi mező
   *  üresen = a törvény szerinti automatikus érték (alap + életkor, gyermek, betegszabadság, arányosítás). */
  szabadsag: any = null;
  szabKezi: Record<string, any> = {};
  szabMentes = false;
  eltartottak: any[] = [];

  szerk: any = null;
  jvSzerk: any = null;
  feorTalalatok: any[] = [];   // FEOR autocomplete találatok
  kedvSzerk: any = null;
  eltSzerk: any = null;

  opt = BER_OPCIOK;

  constructor(private ber: BerService, private toast: ToastController, private alert: AlertController,
              private router: Router, private http: HttpClient, private szerkZar: SzerkZarService) {}

  /*  SZERKESZTÉSI ZÁRAK (components/szerk-zar) szerkesztőnként: a meglévő rekordot egyszerre egy ember
   *  szerkesztheti. A sorszám a késve érkező foglalást dobja el, ha a szerkesztőt közben bezárták. */
  zarAlk: SzerkZar | null = null;
  zarJv: SzerkZar | null = null;
  zarKedv: SzerkZar | null = null;
  zarSzocho: SzerkZar | null = null;
  zarElt: SzerkZar | null = null;
  private zarSorsz: Record<ZarMezo, number> = { zarAlk: 0, zarJv: 0, zarKedv: 0, zarSzocho: 0, zarElt: 0 };
  private async zarFoglal(mezo: ZarMezo, tipus: string, id: string, ujratolt: () => unknown) {
    this.zarEngedi(mezo);
    const s = this.zarSorsz[mezo];
    const z = await this.szerkZar.foglal('ber', tipus, id, { ujratolt });
    if (s !== this.zarSorsz[mezo]) { z?.felenged(); return; }
    this[mezo] = z;
  }
  zarEngedi(mezo: ZarMezo) { this.zarSorsz[mezo]++; this[mezo]?.felenged(); this[mezo] = null; }
  private zarolt(mezo: ZarMezo) { const z = this[mezo]; return !!z && !z.irhato; }
  ngOnDestroy() { for (const m of Object.keys(this.zarSorsz) as ZarMezo[]) this.zarEngedi(m); }

  /*  BIZTOSÍTOTTI BEJELENTÉS / VÁLTOZÁSBEJELENTÉS (T1041).
   *  A választott időszak be- és kilépéseiből előkészíti a bejelentőlapot: a személyi adatok, a
   *  jogviszony kezdete vagy vége, a biztosítás kódja, a FEOR és a heti munkaidő átkerül. A
   *  kitöltőben ellenőrizhető és onnan küldhető be (a NAV-kapcsolatnál beállított környezetbe).
   *  Adatmódosításnál a NAV a „korábbi" és az „új" adatot is kéri – azt a könyvelő tölti. */
  bejelentFut = false;
  async bejelentes() {
    if (!this.aktiv.ugyfelId) return;
    const ma = new Date();
    const elso = new Date(ma.getFullYear(), ma.getMonth(), 1).toISOString().slice(0, 10);
    const a = await this.alert.create({
      header: 'Bejelentés a NAV-hoz (T1041)',
      message: 'Mely időszak be- és kilépéseit vigyük a bejelentőlapra?',
      inputs: [
        { type: 'date', name: 'tol', value: elso },
        { type: 'date', name: 'ig', value: ma.toISOString().slice(0, 10) },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Előkészítés', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok') return;
    const tol = String(v.data?.values?.tol || elso).slice(0, 10);
    const ig = String(v.data?.values?.ig || ma.toISOString().slice(0, 10)).slice(0, 10);
    this.bejelentFut = true;
    try {
      const ny = <T = any>(ut: string, test?: any): Promise<T> => firstValueFrom(test === undefined
        ? this.http.get<T>('/api/nav-nyomtatvany' + ut) : this.http.post<T>('/api/nav-nyomtatvany' + ut, test));
      const katalogus: any = await ny('/katalogus');
      if (!(katalogus.lista || []).some((k: any) => k.kod === 'T1041' && k.letoltve)) await ny('/sablon/T1041/letolt', {});
      const r: any = await ny('/bevallas', {
        modul: 'BER', ugyfel_id: this.aktiv.ugyfelId, kod: 'T1041', ev: Number(tol.slice(0, 4)),
        gyakorisag: 'eves', kitoltesParam: { tol, ig },
        cim: `T1041 – ${tol} – ${ig}`,
      });
      const k = r.kitoltes || {};
      if (!k.esemenyek) this.figyelmeztet('Ebben az időszakban nincs be- vagy kilépés – üres bejelentőlap nyílt meg.');
      else this.ok(`${k.esemenyek} esemény került a bejelentőlapra.`);
      this.router.navigate(['/ber/bevallas', r.id]);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült előkészíteni a bejelentést.'); }
    finally { this.bejelentFut = false; }
  }

  async ngOnInit() {
    await this.ber.init();
    await this.betolt();
  }

  get aktiv() { return this.ber.aktiv; }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    try {
      this.alkalmazottak = await this.ber.get('/alkalmazottak/' + this.aktiv.ugyfelId);
      const ugyfelek: any[] = await this.ber.get('/ugyfelek/' + this.aktiv.intezmeny);
      this.ugyfel = ugyfelek.find((x) => x.id === this.aktiv.ugyfelId) || null;
    }
    catch { this.hiba('Nem sikerült betölteni az alkalmazottakat.'); }
  }

  // Önfoglalkoztató (pl. egyéni vállalkozó) felvétele az ügyfél törzsadataiból. A modal előtöltve
  // nyílik, hogy a hiányzó adatok (TAJ, anyja neve, szül. adatok) pótolhatók legyenek mentés előtt.
  ujAlkUgyfelbol() {
    this.zarEngedi('zarAlk');
    const u = this.ugyfel || {};
    this.szerk = {
      ugyfel_id: this.aktiv.ugyfelId,
      nev: u.nev || '',
      adoazonosito: u.adoazonosito || null,
      irsz: u.irsz || null, telepules: u.telepules || null, cim: u.cim || null,
      bankszamla: u.bankszamla || null, email: u.email || null, telefon: u.telefon || null,
      neme: 'ferfi', allampolgarsag: 'magyar', nyugdijas: false, inaktiv: false,
    };
    this.alkModal.present();
  }

  async valaszt(a: any) {
    this.kivalasztott = a;
    await this.reszletek();
  }
  async reszletek() {
    if (!this.kivalasztott) return;
    if (!this.munkaszamok.length) this.munkaszamBetolt();
    const id = this.kivalasztott.id;
    [this.jogviszonyok, this.kedvezmenyek, this.eltartottak, this.szochoKedvezmenyek] = await Promise.all([
      this.ber.get('/jogviszonyok/' + id), this.ber.get('/kedvezmenyek/' + id), this.ber.get('/eltartottak/' + id),
      this.ber.get('/szocho-kedvezmenyek/' + id).catch(() => []),
    ]);
    await this.szabadsagBetolt();
  }

  private szabKitolt(r: any) {
    this.szabadsag = r && !r.nincs ? r : null;
    const k: Record<string, any> = {};
    if (this.szabadsag) {
      k['elozo_evi'] = r.elozo_kezi ? r.osszesen.elozo : '';
      for (const s of r.sorok) k[s.fajta] = s.kezi ? s.adott : '';
      k['beteg'] = r.beteg.kezi ? r.beteg.keret : '';
      k['megjegyzes'] = r.megjegyzes || '';
    }
    this.szabKezi = k;
  }
  async szabadsagBetolt() {
    if (!this.kivalasztott) return;
    try { this.szabKitolt(await this.ber.get(`/szabadsag-keret/${this.kivalasztott.id}?ev=${this.ber.aktiv.ev}`)); }
    catch { this.szabadsag = null; }
  }
  async szabadsagMent() {
    if (!this.kivalasztott || this.szabMentes) return;
    this.szabMentes = true;
    try {
      this.szabKitolt(await this.ber.put(`/szabadsag-keret/${this.kivalasztott.id}?ev=${this.ber.aktiv.ev}`, this.szabKezi));
      (await this.toast.create({ message: 'A szabadságkeret elmentve.', duration: 2000, color: 'success' })).present();
    } catch (e: any) {
      (await this.toast.create({ message: e?.error?.error || 'A mentés nem sikerült.', duration: 3000, color: 'danger' })).present();
    } finally { this.szabMentes = false; }
  }

  cimke(lista: any[], v: string) { return lista.find((x) => x.value === v)?.label || v; }

  // --- SULINFORM munkatársak importja / összekötése (backend/ber-sulinform.js) ---
  sulLista: any[] = [];
  sulValasztott: Record<string, boolean> = {};
  sulJogviszony = true;
  sulBetolt = false;
  sulIntezmeny = '';
  get sulKijelolt(): string[] { return Object.keys(this.sulValasztott).filter((k) => this.sulValasztott[k]); }
  async nyitSulinform() {
    if (!this.aktiv.ugyfelId) return;
    this.sulBetolt = true; this.sulLista = []; this.sulValasztott = {};
    this.sulModal.present();
    try {
      const r: any = await this.ber.sulinformApi.get('/munkatarsak/' + this.aktiv.ugyfelId);
      this.sulLista = r.munkatarsak || [];
      this.sulIntezmeny = r.feladatellatas?.name || '';
    } catch (e: any) { this.hiba(e?.error?.error || 'A munkatársak lekérése nem sikerült.'); }
    finally { this.sulBetolt = false; }
  }
  sulMindet(be: boolean) { for (const m of this.sulLista) if (!m.alkalmazott_id) this.sulValasztott[m.adid] = be; }
  async sulImport() {
    const adidok = this.sulKijelolt;
    if (!adidok.length) return;
    try {
      const r: any = await this.ber.sulinformApi.post('/import/' + this.aktiv.ugyfelId, { adidok, jogviszony: this.sulJogviszony });
      await this.sulModal.dismiss();
      await this.betolt();
      this.ok(`SULINFORM: ${r.uj} új alkalmazott, ${r.osszekotve} összekötve${r.kihagyva ? ', ' + r.kihagyva + ' kihagyva' : ''}.`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Az import nem sikerült.'); }
  }

  // --- NAV bejelentettek importja (a kapcsolt NAPLO-ügyfél M2M-lekérdezéséből) ---
  async nyitNavImport() {
    if (!this.aktiv.ugyfelId) return;
    this.navBetolt = true;
    this.navBejelentettek = [];
    this.navModal.present();
    try {
      const r: any = await this.ber.get('/nav-bejelentettek/' + this.aktiv.ugyfelId);
      this.navBejelentettek = r.foglalkoztatottak || [];
      this.navUtolsoSzinkron = r.utolso_szinkron || null;
    } catch (e: any) {
      this.hiba(e?.error?.error || 'A bejelentettek lekérése nem sikerült. Előbb a NAPLO Adófolyószámla fülön futtass NAV-szinkront.');
    } finally { this.navBetolt = false; }
  }
  async importNav(f: any) {
    try {
      await this.ber.post('/alkalmazott/import-nav/' + this.aktiv.ugyfelId, { foglalkoztatott: f });
      f.importalva = true;
      await this.betolt();
      this.ok(f.nev + ' importálva.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Az import nem sikerült.'); }
  }

  // --- alkalmazott ---
  ujAlk() { this.zarEngedi('zarAlk'); this.szerk = { ugyfel_id: this.aktiv.ugyfelId, nev: '', neme: 'ferfi', allampolgarsag: 'magyar', nyugdijas: false, inaktiv: false }; this.alkModal.present(); }
  szerkAlk(a: any) {
    this.szerk = this.alkSzerkbol(a); this.alkModal.present();
    this.zarFoglal('zarAlk', 'ber_alkalmazott', a.id, async () => {
      this.alkalmazottak = await this.ber.get('/alkalmazottak/' + this.aktiv.ugyfelId);
      const friss = this.alkalmazottak.find((x) => x.id === a.id);
      if (friss && this.szerk?.id === a.id) this.szerk = this.alkSzerkbol(friss);
    });
  }
  private alkSzerkbol(a: any) { return { ...a, nyugdijas: !!a.nyugdijas, inaktiv: !!a.inaktiv, szul_ido: a.szul_ido ? String(a.szul_ido).slice(0, 10) : null }; }
  async mentAlk() {
    if (!this.szerk?.nev?.trim()) return this.hiba('A név kötelező.');
    if (this.zarolt('zarAlk')) return;
    this.szerk.ugyfel_id = this.aktiv.ugyfelId;
    try {
      if (this.szerk.id) await this.ber.put('/alkalmazott/' + this.szerk.id, this.szerk);
      else await this.ber.post('/alkalmazott', this.szerk);
      await this.alkModal.dismiss(); await this.betolt(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba.'); }
  }
  async torolAlk(a: any) {
    const al = await this.alert.create({ header: 'Törlés', message: `Törli: ${a.nev}?`, buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: async () => {
      try { await this.ber.del('/alkalmazott/' + a.id); } catch (e: any) { return this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
      if (this.kivalasztott?.id === a.id) this.kivalasztott = null; await this.betolt(); this.ok('Törölve.'); } }] });
    al.present();
  }

  // --- jogviszony ---
  /** Az ügyfél munkaszámai (Törzsadatok → Munkaszám törzs) a jogviszonyhoz. */
  munkaszamok: any[] = [];
  private async munkaszamBetolt() {
    try { this.munkaszamok = (await this.ber.torzsApi.get<any[]>('/munkaszamok/' + this.aktiv.ugyfelId)).filter((m) => m.aktiv); } catch { this.munkaszamok = []; }
  }
  munkaszamNev(id: string | null): string { const m = id ? this.munkaszamok.find((x) => x.id === id) : null; return m ? m.kod : ''; }

  /*  Munkaidő nélküli jogviszony (heti 0 óra): a társas vállalkozó tagnál ez az alap. Visszaváltáskor
   *  a szokásos 40/8 óra kerül vissza, hogy ne maradjon 0-s óraszám egy munkaviszonyon. */
  munkarendValt(mr: string) {
    if (!this.jvSzerk) return;
    if (mr === 'nincs') { this.jvSzerk.heti_oraszam = 0; this.jvSzerk.napi_oraszam = 0; }
    else if (!Number(this.jvSzerk.heti_oraszam)) { this.jvSzerk.heti_oraszam = 40; this.jvSzerk.napi_oraszam = 8; }
  }
  jvTipusValt(t: string) {
    if (!this.jvSzerk) return;
    if (t === 'tarsas_vallalkozo' && this.jvSzerk.munkarend !== 'nincs') { this.jvSzerk.munkarend = 'nincs'; this.munkarendValt('nincs'); }
  }
  ujJv() { this.zarEngedi('zarJv'); this.munkaszamBetolt(); this.jvSzerk = { munkaszam_id: null, ugyfel_id: this.aktiv.ugyfelId, alkalmazott_id: this.kivalasztott.id, jogviszony_tipus: 'munkaviszony', biztositasi_jelleg: 'foallasu', ber_tipus: 'havi', munkarend: 'altalanos', heti_oraszam: 40, napi_oraszam: 8, alapber: null, atalany_koltseghanyad: 45, garantalt: false, biztositott: true, aktiv: true, ekho: false, ekho_kulcs: null, ekho_ertekhatar: 1, ekho_minimalber_kivetel: false, megbizas_koltseghanyad: 10, tartos_megbizas: false }; this.feorTalalatok = []; this.jvModal.present(); }
  szerkJv(j: any) {
    this.munkaszamBetolt(); this.jvSzerk = this.jvSzerkbol(j); this.feorTalalatok = []; this.jvModal.present();
    this.zarFoglal('zarJv', 'ber_jogviszony', j.id, async () => {
      const friss = (await this.ber.get<any[]>('/jogviszonyok/' + (j.alkalmazott_id || this.kivalasztott?.id))).find((x) => x.id === j.id);
      if (friss && this.jvSzerk?.id === j.id) this.jvSzerk = this.jvSzerkbol(friss);
    });
  }
  private jvSzerkbol(j: any) {
    return { ...j, munkaszam_id: j.munkaszam_id || null, biztositasi_jelleg: j.biztositasi_jelleg || 'foallasu', atalany_koltseghanyad: Number(j.atalany_koltseghanyad) || 45, garantalt: !!j.garantalt, biztositott: j.biztositott !== 0, aktiv: j.aktiv !== 0, kezdet: j.kezdet ? String(j.kezdet).slice(0, 10) : null, veg: j.veg ? String(j.veg).slice(0, 10) : null,
      ekho: !!Number(j.ekho), ekho_kulcs: j.ekho_kulcs === 'egt' ? 'egt' : null, ekho_ertekhatar: Number(j.ekho_ertekhatar) || 1, ekho_minimalber_kivetel: !!Number(j.ekho_minimalber_kivetel),
      megbizas_koltseghanyad: j.megbizas_koltseghanyad == null ? 10 : Number(j.megbizas_koltseghanyad), tartos_megbizas: !!Number(j.tartos_megbizas) };
  }
  get jvEv() { return this.jvSzerk?.jogviszony_tipus === 'egyeni_vallalkozo'; }

  // FEOR autocomplete (a backend a KSH 485-ös FEOR-08 listájából keres kód/név alapján).
  async feorKeres(term: string) {
    this.jvSzerk.feor = term;
    if (!term || !term.trim()) { this.feorTalalatok = []; return; }
    try { this.feorTalalatok = await this.ber.get('/feor?q=' + encodeURIComponent(term.trim())); }
    catch { this.feorTalalatok = []; }
  }
  feorValaszt(f: any) {
    this.jvSzerk.feor = f.kod;
    this.jvSzerk.feor_nev = f.nev;
    // Ha a munkakör még ÜRES, töltsük a FEOR megnevezésével (a legtöbbször ez a munkakör neve is).
    // A felhasználó szabadon felülírhatja; a már kitöltött munkakört SOHA nem írjuk felül.
    if (!String(this.jvSzerk.munkakor ?? '').trim()) this.jvSzerk.munkakor = f.nev;
    this.feorTalalatok = [];
  }
  async mentJv() {
    // Az alapbér BÁRMELY jogviszonynál lehet 0 (pl. fizetés nélküli szabadság, díjazás nélküli tagi
    // jogviszony, EV – ahol a bevétel alapján számolunk az Átalányadós fülön). A 0 tehát ÉRVÉNYES
    // érték: korábban a `!alapber` a 0-t is „hiányzónak” vette (JS-ben falsy), ezért nem lehetett
    // menteni. Csak azt jelezzük, ha a mezőt ÜRESEN hagyták.
    if (this.zarolt('zarJv')) return;
    const ab = this.jvSzerk?.alapber;
    if (!this.jvEv && (ab === null || ab === undefined || String(ab).trim() === '')) {
      return this.hiba('Az alapbér kötelező (0 is lehet).');
    }
    if (this.jvEv) this.jvSzerk.alapber = 0;
    try {
      let jvId = this.jvSzerk.id;
      if (jvId) await this.ber.put('/jogviszony/' + jvId, this.jvSzerk);
      else jvId = (await this.ber.post<any>('/jogviszony', this.jvSzerk))?.id;
      if (jvId) await this.ber.torzsApi.put('/jogviszony-munkaszam/' + jvId, { munkaszam_id: this.jvSzerk.munkaszam_id || null });
      await this.jvModal.dismiss(); await this.reszletek(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba.'); }
  }
  async torolJv(j: any) {
    try { await this.ber.del('/jogviszony/' + j.id); } catch (e: any) { return this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
    await this.reszletek(); this.ok('Törölve.');
  }

  // --- kedvezmény ---
  ujKedv() { this.zarEngedi('zarKedv'); this.kedvSzerk = { alkalmazott_id: this.kivalasztott.id, tipus: 'csaladi', eltartottak: 1, kedvezmenyezett: 1, megoszt_szazalek: 100 }; this.kedvModal.present(); }
  szerkKedv(k: any) {
    this.kedvSzerk = this.kedvSzerkbol(k); this.kedvModal.present();
    this.zarFoglal('zarKedv', 'ber_kedvezmeny', k.id, async () => {
      const friss = (await this.ber.get<any[]>('/kedvezmenyek/' + (k.alkalmazott_id || this.kivalasztott?.id))).find((x) => x.id === k.id);
      if (friss && this.kedvSzerk?.id === k.id) this.kedvSzerk = this.kedvSzerkbol(friss);
    });
  }
  private kedvSzerkbol(k: any) { return { ...k, ervenyes_tol: k.ervenyes_tol ? String(k.ervenyes_tol).slice(0, 10) : null, ervenyes_ig: k.ervenyes_ig ? String(k.ervenyes_ig).slice(0, 10) : null }; }
  async mentKedv() {
    if (this.zarolt('zarKedv')) return;
    try {
      if (this.kedvSzerk.id) await this.ber.put('/kedvezmeny/' + this.kedvSzerk.id, this.kedvSzerk);
      else await this.ber.post('/kedvezmeny', this.kedvSzerk);
      await this.kedvModal.dismiss(); await this.reszletek(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba.'); }
  }
  async torolKedv(k: any) {
    try { await this.ber.del('/kedvezmeny/' + k.id); } catch (e: any) { return this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
    await this.reszletek(); this.ok('Törölve.');
  }

  /*  MUNKÁLTATÓI (SZOCHO) KEDVEZMÉNY. A típus kiválasztásakor felajánljuk a törvény szerinti
   *  alapértelmezett korlátot és mértéket – a lépcsők (pl. első év 100%, utána 50%) külön
   *  időszakként rögzítendők. */
  szochoSzerk: any = null;
  /*  A TÖRVÉNY SZERINTI ALAPÉRTÉKEK (Szocho tv., NAV 2026. 01. 16-i információs füzet, 3.1–3.9.).
   *  A „százalék" azt mondja meg, az ADÓ (13%) hány százaléka engedhető el – nem a bér %-a:
   *    10. §  szakképzettséget nem igénylő / mezőgazdasági: minimálbérig, az adómérték 50%-a,
   *    11. §  munkaerőpiacra lépő: minimálbérig 100% (1. év), utána fél évig 50%,
   *    12. §  3+ gyermekes munkaerőpiacra lépő nő: minimálbérig 100% (1–3. év), 4–5. évben 50%,
   *    13. §  megváltozott munkaképességű: a minimálbér KÉTSZERESÉIG 100%,
   *    15. §  kutató: a minimálbér NÉGYSZERESÉIG 100%,
   *    16. §  K+F tevékenység: a bérköltség után az adómérték 50%-a.
   *  A lépcsőket (pl. első év 100%, utána 50%) külön időszakként kell felvenni. */
  readonly szochoAlap: Record<string, { korlat: string; szazalek: number }> = {
    szakkepzettseg_nelkul: { korlat: 'minimalber', szazalek: 50 },
    munkaeropiac: { korlat: 'minimalber', szazalek: 100 },
    anya3_munkaeropiac: { korlat: 'minimalber', szazalek: 100 },
    megvaltozott: { korlat: 'ketszeres_minimalber', szazalek: 100 },
    kutato: { korlat: 'negyszeres_minimalber', szazalek: 100 },
    kutatas: { korlat: 'nincs', szazalek: 50 },
    vedett_korban: { korlat: 'minimalber', szazalek: 100 },
    dualis: { korlat: 'minimalber', szazalek: 100 },
    egyeb: { korlat: 'minimalber', szazalek: 100 },
  };
  ujSzocho() {
    this.zarEngedi('zarSzocho');
    this.szochoSzerk = { alkalmazott_id: this.kivalasztott.id, tipus: 'munkaeropiac', alap_korlat: 'minimalber', szazalek: 100, ervenyes_tol: null, ervenyes_ig: null };
    this.szochoModal.present();
  }
  szerkSzocho(k: any) {
    this.szochoSzerk = { ...k }; this.szochoModal.present();
    this.zarFoglal('zarSzocho', 'ber_szocho_kedvezmeny', k.id, async () => {
      const friss = (await this.ber.get<any[]>('/szocho-kedvezmenyek/' + (k.alkalmazott_id || this.kivalasztott?.id))).find((x) => x.id === k.id);
      if (friss && this.szochoSzerk?.id === k.id) this.szochoSzerk = { ...friss };
    });
  }
  szochoTipusValt() {
    const a = this.szochoAlap[this.szochoSzerk?.tipus];
    if (a) { this.szochoSzerk.alap_korlat = a.korlat; this.szochoSzerk.szazalek = a.szazalek; }
  }
  async mentSzocho() {
    if (this.zarolt('zarSzocho')) return;
    try {
      if (this.szochoSzerk.id) await this.ber.put('/szocho-kedvezmeny/' + this.szochoSzerk.id, this.szochoSzerk);
      else await this.ber.post('/szocho-kedvezmeny', this.szochoSzerk);
      await this.szochoModal.dismiss(); await this.reszletek(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async torolSzocho(k: any) {
    try { await this.ber.del('/szocho-kedvezmeny/' + k.id); } catch (e: any) { return this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
    await this.reszletek(); this.ok('Törölve.');
  }

  // --- eltartott ---
  ujElt() { this.zarEngedi('zarElt'); this.eltSzerk = { alkalmazott_id: this.kivalasztott.id, nev: '', jogcim: 'kedvezmenyezett', adoazonosito: null, szul_ido: null }; this.eltModal.present(); }
  szerkElt(e: any) {
    this.eltSzerk = this.eltSzerkbol(e); this.eltModal.present();
    this.zarFoglal('zarElt', 'ber_eltartott', e.id, async () => {
      const friss = (await this.ber.get<any[]>('/eltartottak/' + (e.alkalmazott_id || this.kivalasztott?.id))).find((x) => x.id === e.id);
      if (friss && this.eltSzerk?.id === e.id) this.eltSzerk = this.eltSzerkbol(friss);
    });
  }
  private eltSzerkbol(e: any) { return { ...e, szul_ido: e.szul_ido ? String(e.szul_ido).slice(0, 10) : null, ervenyes_tol: e.ervenyes_tol ? String(e.ervenyes_tol).slice(0, 10) : null, ervenyes_ig: e.ervenyes_ig ? String(e.ervenyes_ig).slice(0, 10) : null }; }
  async mentElt() {
    if (!this.eltSzerk?.nev?.trim()) return this.hiba('A név kötelező.');
    if (this.zarolt('zarElt')) return;
    try {
      if (this.eltSzerk.id) await this.ber.put('/eltartott/' + this.eltSzerk.id, this.eltSzerk);
      else await this.ber.post('/eltartott', this.eltSzerk);
      await this.eltModal.dismiss(); await this.reszletek(); this.ok('Mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Hiba.'); }
  }
  async torolElt(e: any) {
    try { await this.ber.del('/eltartott/' + e.id); } catch (err: any) { return this.hiba(err?.error?.error || 'Nem sikerült törölni.'); }
    await this.reszletek(); this.ok('Törölve.');
  }
  get kedvezmenyezettSzam() { return this.eltartottak.filter((e) => e.jogcim === 'kedvezmenyezett').length + 0.5 * this.eltartottak.filter((e) => e.jogcim === 'felvaltva').length; }

  /*  A letiltásoknak saját képernyője van (Bér → Letiltások): egy végrehajtási ügy több tételből
   *  áll, tételenként más kedvezményezettel, kielégítési sorrenddel és havi kimutatással. */
  letiltasokra() { this.router.navigateByUrl('/ber/letiltasok'); }

  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 4200, color: 'warning', position: 'top' })).present(); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
